import { t } from "@/i18n-js/instance";

const passwordStrengthStatuses = {
  great: "great",
  better: "better",
  weak: "weak",
  none: "none",
} as const;

export interface PasswordStrength {
  percentage: number;
  progressBarColor: string;
  requirementsMet: {
    uppercase: boolean;
    number: boolean;
    symbol: boolean;
    length: boolean;
  };
  status: (typeof passwordStrengthStatuses)[keyof typeof passwordStrengthStatuses];
  strengthMessage: string;
}

const MIN_LENGTH = 6;

export const isGreatPassword = (passwordStrength: PasswordStrength) =>
  passwordStrength.status === passwordStrengthStatuses.great;

const calculatePasswordRequirementsMet = (
  password: string,
): PasswordStrength["requirementsMet"] => ({
  uppercase: /[A-Z]/.test(password),
  number: /\d/.test(password),
  symbol: /\W/.test(password),
  length: password.length >= MIN_LENGTH,
});

const calculatePasswordMetrics = (
  password: string,
  requirementsMet: PasswordStrength["requirementsMet"],
): Pick<
  PasswordStrength,
  "status" | "percentage" | "strengthMessage" | "progressBarColor"
> => {
  let percentage: PasswordStrength["percentage"] = 0;
  // eslint-disable-next-line sonarjs/no-dead-store -- Disabled for plugin version update. Fix when possible.
  let status: PasswordStrength["status"] = passwordStrengthStatuses.none;
  let strengthMessage = "";
  // eslint-disable-next-line sonarjs/no-dead-store -- Disabled for plugin version update. Fix when possible.
  let progressBarColor: PasswordStrength["progressBarColor"] = "bg-neutral-200";

  const hasMinLength = requirementsMet.length;
  const characterTypeMetCount = [
    requirementsMet.uppercase,
    requirementsMet.number,
    requirementsMet.symbol,
  ].filter(Boolean).length;

  const passwordStrengthLocaleScope = "paywalls.checkout.password_strength";

  if (!password) {
    status = passwordStrengthStatuses.none;
    // eslint-disable-next-line sonarjs/no-redundant-assignments -- Disabled for plugin version update. Fix when possible.
    percentage = 0;
    // eslint-disable-next-line sonarjs/no-redundant-assignments -- Disabled for plugin version update. Fix when possible.
    strengthMessage = "";
    // eslint-disable-next-line sonarjs/no-redundant-assignments -- Disabled for plugin version update. Fix when possible.
    progressBarColor = "bg-neutral-200";
  } else if (hasMinLength && characterTypeMetCount === 3) {
    status = passwordStrengthStatuses.great;
    percentage = 100;
    strengthMessage = t(`${passwordStrengthLocaleScope}.great`);
    progressBarColor = "bg-green-600";
  } else if (hasMinLength && characterTypeMetCount >= 1) {
    status = passwordStrengthStatuses.better;
    percentage = 20;
    strengthMessage = t(`${passwordStrengthLocaleScope}.better`);
    progressBarColor = "bg-amber-400";
  } else {
    status = passwordStrengthStatuses.weak;
    percentage = 10;
    strengthMessage = t(`${passwordStrengthLocaleScope}.weak`);
    progressBarColor = "bg-red-500";
  }

  return {
    status,
    percentage,
    strengthMessage,
    progressBarColor,
  };
};

export const calculatePasswordStrength = (
  password: string,
): PasswordStrength => {
  const requirementsMet = calculatePasswordRequirementsMet(password);
  const passwordMetrics = calculatePasswordMetrics(password, requirementsMet);

  return {
    ...passwordMetrics,
    requirementsMet: requirementsMet,
  };
};

export const getPasswordRequirementMessage = (
  passwordStrength: PasswordStrength,
  passwordEntered: boolean,
  hideSuccessMessage = false,
): string => {
  const passwordRequirementLocaleScope =
    "paywalls.checkout.password_requirement";

  if (isGreatPassword(passwordStrength)) {
    return hideSuccessMessage
      ? ""
      : t(`${passwordRequirementLocaleScope}.success_message`);
  }

  const messages = [];

  if (!passwordStrength.requirementsMet.length || !passwordEntered) {
    messages.push(
      t(`${passwordRequirementLocaleScope}.min_length`, { count: MIN_LENGTH }),
    );
  }

  if (passwordEntered) {
    if (!passwordStrength.requirementsMet.uppercase)
      messages.push(t(`${passwordRequirementLocaleScope}.uppercase`));
    if (!passwordStrength.requirementsMet.number)
      messages.push(t(`${passwordRequirementLocaleScope}.number`));
    if (!passwordStrength.requirementsMet.symbol)
      messages.push(t(`${passwordRequirementLocaleScope}.symbol`));
  } else {
    messages.push(t(`${passwordRequirementLocaleScope}.uppercase`));
    messages.push(t(`${passwordRequirementLocaleScope}.number`));
    messages.push(t(`${passwordRequirementLocaleScope}.symbol`));
  }

  return t(`${passwordRequirementLocaleScope}.prefix`, {
    requirements: messages.join(", "),
  });
};
